import React, { useState, useRef, useEffect } from 'react';
import { getReportLimits, getReportCountStatusColor } from '../utils/billingHelpers';

const ReportsList = ({ reports, workspaceId, onDeleteReport, loading, selectedFile, onFileSelect, onUpload, workspaceName, subscriptionData, isSuspended = false }) => {
const [copiedReportId, setCopiedReportId] = useState(null);
const [searchQuery, setSearchQuery] = useState('');
const [showUploadModal, setShowUploadModal] = useState(false);
const [showEmbeddingModal, setShowEmbeddingModal] = useState(false);
const [selectedEmbeddingReport, setSelectedEmbeddingReport] = useState(null);
const [copiedWorkspaceId, setCopiedWorkspaceId] = useState(false);
const [copiedEmbedReportId, setCopiedEmbedReportId] = useState(false);
const [showHelpSection, setShowHelpSection] = useState(false);
const fileInputRef = useRef(null);
const previousFileRef = useRef(null);
  
  // Close modal when file is cleared from parent (after successful upload/overwrite)
  // Only close if file was previously selected and is now null (indicates successful upload)
  useEffect(() => {
    // Only auto-close if: had a file before, no file now, AND modal is currently open
    // This handles successful upload cleanup
    if (previousFileRef.current && !selectedFile && showUploadModal) {
      setShowUploadModal(false);
    }
    previousFileRef.current = selectedFile;
  }, [selectedFile, showUploadModal]);
  
  // Get viewer base URL from environment variables
  const getViewerBaseUrl = () => {
    // Prefer a unified base URL, else fallback to env-specific values
    const unified = import.meta.env.VITE_VIEWER_BASE_URL;
    if (unified) return unified;
    const dev = import.meta.env.VITE_VIEWER_BASE_URL_DEV;
    const prod = import.meta.env.VITE_VIEWER_BASE_URL_PROD;
    // Use dev when running locally, else prod
    const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';
    return (isLocal ? dev : prod) || (isLocal ? 'https://localhost:7000/pbi' : '/pbi');
  };

  // Build report URL: baseUrl/workspaceId/reportId
  const buildReportUrl = (reportId) => {
    if (!workspaceId) return 'Workspace ID not available';
    const baseUrl = getViewerBaseUrl();
    return `${baseUrl}/${workspaceId}/${reportId}`;
  };

  // Copy URL to clipboard
  const copyToClipboard = async (url, reportId) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedReportId(reportId);
      setTimeout(() => setCopiedReportId(null), 2000);
    } catch (err) {
      alert('Failed to copy URL to clipboard');
    }
  };

  // Handle embedding button click
  const handleEmbeddingClick = (report) => {
    setSelectedEmbeddingReport(report);
    setShowEmbeddingModal(true);
  };

  // Copy workspace ID to clipboard
  const copyWorkspaceId = async () => {
    try {
      await navigator.clipboard.writeText(workspaceId);
      setCopiedWorkspaceId(true);
      setTimeout(() => setCopiedWorkspaceId(false), 2000);
    } catch (err) {
      alert('Failed to copy Workspace ID to clipboard');
    }
  };

  // Copy report ID to clipboard (for embedding modal)
  const copyEmbedReportId = async (reportId) => {
    try {
      await navigator.clipboard.writeText(reportId);
      setCopiedEmbedReportId(true);
      setTimeout(() => setCopiedEmbedReportId(false), 2000);
    } catch (err) {
      alert('Failed to copy Report ID to clipboard');
    }
  };

  // File upload handlers
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.pbix')) {
      onFileSelect(file);
    } else {
      alert('Please select a valid PBIX file');
      event.target.value = '';
    }
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.name.endsWith('.pbix')) {
      onFileSelect(file);
    } else {
      alert('Please drop a valid PBIX file');
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleUploadClick = async () => {
    try {
      await onUpload();
      // Only close modal and clear file if upload succeeds
      setShowUploadModal(false);
      onFileSelect(null);
    } catch (error) {
      // If it's a duplicate error, CLOSE upload modal to show overwrite confirmation
      if (error.message === 'DUPLICATE_REPORT') {
        setShowUploadModal(false); // Close upload modal so confirmation dialog is visible
        // Don't clear file - user needs it for overwrite
        return;
      }
      // For other errors, let parent handle but don't close modal
    }
  };

  // Render upload modal - MOVED BEFORE EARLY RETURN
  const renderUploadModal = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '8px',
        padding: '2rem',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>Upload New Report</h2>
          <button
            onClick={() => {
              setShowUploadModal(false);
              onFileSelect(null);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6c757d'
            }}
          >
          </button>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#495057' }}>
            Select PBIX File
          </label>
          <div
            onDrop={handleFileDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: '2px dashed #ced4da',
              borderRadius: '8px',
              padding: '2rem',
              textAlign: 'center',
              cursor: 'pointer',
              background: selectedFile ? '#e8f5e8' : '#fff',
              borderColor: selectedFile ? '#28a745' : '#ced4da',
              transition: 'all 0.3s ease'
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pbix"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            {selectedFile ? (
              <div>
                <p style={{ margin: '0', color: '#28a745', fontWeight: 'bold' }}>
                  Selected: {selectedFile.name}
                </p>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#6c757d' }}>
                  File size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            ) : (
              <div>
                <p style={{ margin: '0', fontWeight: 'bold' }}>Click here or drag & drop a PBIX file</p>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#6c757d' }}>
                  Only .pbix files are supported
                </p>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setShowUploadModal(false);
              onFileSelect(null);
            }}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleUploadClick}
            disabled={loading || !selectedFile}
          >
            {loading ? 'Uploading...' : 'Upload Report'}
          </button>
        </div>
      </div>
    </div>
  );

  // Render embedding info modal
  const renderEmbeddingModal = () => {
    if (!selectedEmbeddingReport) return null;

    const reportName = selectedEmbeddingReport.ReportName || selectedEmbeddingReport.reportName;
    const reportId = selectedEmbeddingReport.ReportID || selectedEmbeddingReport.reportID;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '600px',
          width: '90%',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
        }}>
          {/* Header */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ marginBottom: '0.5rem' }}>
              <h2 style={{ margin: 0, color: '#333' }}>Embedding Information</h2>
            </div>
            <p style={{ margin: 0, color: '#666', fontSize: '0.95rem' }}>
              Use these IDs to embed <strong>{reportName}</strong> in your application
            </p>
          </div>

          {/* Workspace ID */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#495057' }}>
              Workspace ID
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                value={workspaceId || 'Not available'}
                readOnly
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  background: '#f8f9fa',
                  fontSize: '0.9rem',
                  color: '#495057',
                  fontFamily: 'monospace'
                }}
              />
              <button
                onClick={copyWorkspaceId}
                disabled={!workspaceId}
                style={{
                  padding: '0.75rem 1.25rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  background: copiedWorkspaceId ? '#28a745' : 'white',
                  color: copiedWorkspaceId ? 'white' : '#495057',
                  cursor: workspaceId ? 'pointer' : 'not-allowed',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  opacity: workspaceId ? 1 : 0.5
                }}
              >
                {copiedWorkspaceId ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Report ID */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#495057' }}>
              Report ID
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                value={reportId}
                readOnly
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  background: '#f8f9fa',
                  fontSize: '0.9rem',
                  color: '#495057',
                  fontFamily: 'monospace'
                }}
              />
              <button
                onClick={() => copyEmbedReportId(reportId)}
                style={{
                  padding: '0.75rem 1.25rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  background: copiedEmbedReportId ? '#28a745' : 'white',
                  color: copiedEmbedReportId ? 'white' : '#495057',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
              >
                {copiedEmbedReportId ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Info Message */}
          <div style={{
            padding: '1rem',
            background: '#e3f2fd',
            border: '1px solid #90caf9',
            borderRadius: '6px',
            marginBottom: '1.5rem'
          }}>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#1565c0', lineHeight: '1.5' }}>
              <strong>Note:</strong> To embed this report in your application, you'll need to configure customer embedding in the <strong>Embedding</strong> tab and use these IDs in your embed token API requests.
            </p>
          </div>

          {/* Close Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              className="btn btn-primary"
              onClick={() => {
                setShowEmbeddingModal(false);
                setSelectedEmbeddingReport(null);
                setCopiedWorkspaceId(false);
                setCopiedEmbedReportId(false);
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Get report limits for display (moved before early return)
  const reportLimits = subscriptionData ? getReportLimits(subscriptionData) : null;
  const reportCountColor = reportLimits && !reportLimits.isUnlimitedReports 
    ? getReportCountStatusColor(reportLimits.currentReportCount, reportLimits.maxReportCount)
    : '#28a745';

  if (!reports || reports.length === 0) {
    return (
      <div className="section">
        {/* Subscription Suspended Warning */}
        {isSuspended && (
          <div style={{ 
            background: '#fff3cd', 
            border: '2px solid #ffc107', 
            padding: '1.5rem', 
            marginBottom: '1.5rem',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16" style={{ color: '#856404', flexShrink: 0 }}>
              <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
            </svg>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}>Subscription Suspended</h3>
              <p style={{ margin: 0, color: '#856404' }}>
                Your subscription is currently suspended. All management functions are disabled and users will be unable to access their reports until this is resolved. 
                Please contact Microsoft support to resolve billing or payment issues.
              </p>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '0.5rem' }}>
          <h2 style={{ margin: 0 }}>Reports</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search reports..."
              style={{ padding: '0.5rem 0.75rem', border: '1px solid #dee2e6', borderRadius: '6px', minWidth: '220px', fontSize: '14px', height: '38px', boxSizing: 'border-box' }}
              disabled={isSuspended}
            />
            {workspaceName && (
              <button
                className="btn btn-primary"
                onClick={() => setShowUploadModal(true)}
                disabled={loading || isSuspended}
                style={{ height: '38px', padding: '0.5rem 0.75rem', boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', margin: 0 }}
                title={isSuspended ? 'Disabled - Subscription Suspended' : 'Upload Report'}
              >
                Upload Report
              </button>
            )}
          </div>
        </div>

        {/* Link to Viewer App Homepage */}
        {(() => {
          const viewerBaseUrl = getViewerBaseUrl();
          return viewerBaseUrl ? (
            <div style={{ 
              background: '#e3f2fd', 
              padding: '1rem', 
              marginBottom: '1rem', 
              border: '1px solid #90caf9',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span style={{ fontSize: '0.95rem', color: '#1B394B', fontWeight: '500' }}>
                Access the Viewer App to view your reports
              </span>
              <button
                onClick={() => window.open(viewerBaseUrl, '_blank')}
                className="btn btn-primary"
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.9rem',
                  whiteSpace: 'nowrap'
                }}
              >
                Open Viewer App
              </button>
            </div>
          ) : null;
        })()}

        {/* Help: Ways to View Reports */}
        <div style={{ 
          background: '#e3f2fd', 
          padding: '0', 
          marginBottom: '1rem', 
          border: '1px solid #90caf9',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
            <div 
              onClick={() => setShowHelpSection(!showHelpSection)}
              style={{ 
                padding: '1rem',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                userSelect: 'none'
              }}
            >
            <span style={{ fontSize: '0.95rem', color: '#1B394B', fontWeight: '500' }}>
              How users can access their reports
            </span>
            <span style={{ 
              color: '#1B394B', 
              fontSize: '1.6rem',
              display: 'inline-block',
              transform: showHelpSection ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}>
              &#9662;
            </span>
          </div>
          {showHelpSection && (
            <div style={{ 
              padding: '0 1rem 1rem 1rem',
              fontSize: '0.9rem', 
              color: '#1B394B', 
              lineHeight: '1.6'
            }}>
              <p style={{ margin: '0.75rem 0 0.75rem 0' }}>
                <strong>1. Direct Report URL:</strong> Provide the Report URL from the table below directly to your users. This opens the report in a full-screen browser window with Export and Personal Bookmark features enabled.
              </p>
              <p style={{ margin: '0 0 0.75rem 0' }}>
                <strong>2. App Viewer URL:</strong> Provide users with the App Viewer URL (accessible via the "Open Viewer App" button above). This displays a simple, unbranded portal listing all reports assigned to the user. Users can copy URLs or open reports from the list. Reports opened this way include Export and Personal Bookmark features.
              </p>
              <p style={{ margin: '0' }}>
                <strong>3. Embedded Reports:</strong> Use the Embed functionality (accessible via the "Embed" button in the table below) to directly embed reports into your own applications as configured in the Embedding tab. Note that embedded reports do not provide Export or Personal Bookmark features, as these are application add-ons. However, you could develop your own solutions for these features.
              </p>
            </div>
          )}
        </div>

        {/* Report License Summary */}
        {reportLimits && (
          <div style={{ 
            background: '#e8f4fd', 
            padding: '1rem', 
            marginBottom: '1rem', 
            border: '1px solid #bee5eb',
            borderRadius: '8px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0 }}>Report License Summary</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
                <strong>Purchased:</strong>{' '}
                <span style={{ color: '#1B394B' }}>
                  {reportLimits.isUnlimitedReports ? 'Unlimited' : reportLimits.purchasedReportCount}
                </span>
              </div>
              <div>
                <strong>Active:</strong>{' '}
                <span style={{ color: '#1B394B' }}>
                  {reportLimits.currentReportCount}
                </span>
              </div>
            <div>
              <strong>Available:</strong>{' '}
              <span style={{ color: '#1B394B' }}>
                {reportLimits.isUnlimitedReports ? 'Unlimited' : (
                  reportLimits.remainingReportSlots > 0 ? reportLimits.remainingReportSlots : '0 (At Limit)'
                )}
              </span>
            </div>
            <div>
              <strong>Max Report Size:</strong>{' '}
              <span style={{ color: '#1B394B' }}>
                {reportLimits.isUnlimitedSize ? 'Unlimited' : `${reportLimits.maxReportSizeMB} MB`}
              </span>
            </div>
          </div>
          {reportLimits.isAtReportLimit && (
            <p style={{ margin: '0.75rem 0 0 0', color: '#dc3545', fontWeight: 'normal' }}>
              <strong>Warning:</strong> You have reached your plan limit. Please delete a report or upgrade your plan to upload more.
            </p>
          )}
        </div>
      )}

      <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
        <p>No reports found in this workspace.</p>
          <p style={{ fontSize: '0.9rem' }}>Upload a PBIX file to get started.</p>
        </div>

        {/* Upload Modal */}
        {showUploadModal && renderUploadModal()}
      </div>
    );
  }

  // Filter reports
  const filteredReports = (reports || []).filter(r => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const name = (r.ReportName || r.reportName || '').toLowerCase();
    const id = (r.ReportID || r.reportID || '').toLowerCase();
    return name.includes(q) || id.includes(q);
  });

  return (
    <div className="section">
      {/* Subscription Suspended Warning */}
      {isSuspended && (
        <div style={{ 
          background: '#fff3cd', 
          border: '2px solid #ffc107', 
          padding: '1.5rem', 
          marginBottom: '1.5rem',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16" style={{ color: '#856404', flexShrink: 0 }}>
            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
          </svg>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}>Subscription Suspended</h3>
            <p style={{ margin: 0, color: '#856404' }}>
              Your subscription is currently suspended. All management functions are disabled and users will be unable to access their reports until this is resolved. 
              Please contact Microsoft support to resolve billing or payment issues.
            </p>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>Reports ({filteredReports.length})</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search reports by name or ID"
            style={{ padding: '0.5rem 0.75rem', border: '1px solid #dee2e6', borderRadius: '6px', minWidth: '260px', fontSize: '14px', height: '38px', boxSizing: 'border-box' }}
            disabled={isSuspended}
          />
          {workspaceName && (
            <button
              className="btn btn-primary"
              onClick={() => setShowUploadModal(true)}
              disabled={loading || isSuspended}
              style={{ height: '38px', padding: '0.5rem 0.75rem', boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', margin: 0 }}
              title={isSuspended ? 'Disabled - Subscription Suspended' : 'Upload Report'}
            >
              Upload Report
            </button>
          )}
        </div>
      </div>

      {/* Link to Viewer App Homepage */}
      {(() => {
        const viewerBaseUrl = getViewerBaseUrl();
        return viewerBaseUrl ? (
          <div style={{ 
            background: '#e3f2fd', 
            padding: '1rem', 
            marginBottom: '1rem', 
            border: '1px solid #90caf9',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ fontSize: '0.95rem', color: '#1B394B', fontWeight: '500' }}>
              Access the Viewer App to view your reports
            </span>
            <button
              onClick={() => window.open(viewerBaseUrl, '_blank')}
              className="btn btn-primary"
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.9rem',
                whiteSpace: 'nowrap'
              }}
            >
              Open Viewer App
            </button>
          </div>
        ) : null;
      })()}

      {/* Help: Ways to View Reports */}
      <div style={{ 
        background: '#e3f2fd', 
        padding: '0', 
        marginBottom: '1rem', 
        border: '1px solid #90caf9',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <div 
          onClick={() => setShowHelpSection(!showHelpSection)}
          style={{ 
            padding: '1rem',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            userSelect: 'none'
          }}
        >
            <span style={{ fontSize: '0.95rem', color: '#1B394B', fontWeight: '500' }}>
              How users can access their reports
            </span>
          <span style={{ 
            color: '#1B394B', 
            fontSize: '1.6rem',
            display: 'inline-block',
            transform: showHelpSection ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            &#9662;
          </span>
        </div>
        {showHelpSection && (
          <div style={{ 
            padding: '0 1rem 1rem 1rem',
            fontSize: '0.9rem', 
            color: '#1B394B', 
            lineHeight: '1.6'
          }}>
            <p style={{ margin: '0.75rem 0 0.75rem 0' }}>
              <strong>1. Direct Report URL:</strong> Provide the Report URL from the table below directly to your users. This opens the report in a full-screen browser window with Export and Personal Bookmark features enabled.
            </p>
            <p style={{ margin: '0 0 0.75rem 0' }}>
              <strong>2. App Viewer URL:</strong> Provide users with the App Viewer URL (accessible via the "Open Viewer App" button above). This displays a simple, unbranded portal listing all reports assigned to the user. Users can copy URLs or open reports from the list. Reports opened this way include Export and Personal Bookmark features.
            </p>
            <p style={{ margin: '0' }}>
              <strong>3. Embedded Reports:</strong> Use the Embed functionality (accessible via the "Embed" button in the table below) to directly embed reports into your own applications as configured in the Embedding tab. Note that embedded reports do not provide Export or Personal Bookmark features, as these are application add-ons. However, you could develop your own solutions for these features.
            </p>
          </div>
        )}
      </div>

      {/* Report License Summary */}
      {reportLimits && (
        <div style={{ 
          background: '#e8f4fd', 
          padding: '1rem', 
          marginBottom: '1rem', 
          border: '1px solid #bee5eb',
          borderRadius: '8px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 style={{ margin: 0 }}>Report License Summary</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <strong>Purchased:</strong>{' '}
              <span style={{ color: '#1B394B' }}>
                {reportLimits.isUnlimitedReports ? 'Unlimited' : reportLimits.purchasedReportCount}
              </span>
            </div>
            <div>
              <strong>Active:</strong>{' '}
              <span style={{ color: '#1B394B' }}>
                {reportLimits.currentReportCount}
              </span>
            </div>
            <div>
              <strong>Available:</strong>{' '}
              <span style={{ color: '#1B394B' }}>
                {reportLimits.isUnlimitedReports ? 'Unlimited' : (
                  reportLimits.remainingReportSlots > 0 ? reportLimits.remainingReportSlots : '0 (At Limit)'
                )}
              </span>
            </div>
            <div>
              <strong>Max Report Size:</strong>{' '}
              <span style={{ color: '#1B394B' }}>
                {reportLimits.isUnlimitedSize ? 'Unlimited' : `${reportLimits.maxReportSizeMB} MB`}
              </span>
            </div>
          </div>
          {reportLimits.isAtReportLimit && (
            <p style={{ margin: '0.75rem 0 0 0', color: '#dc3545', fontWeight: 'normal' }}>
              <strong>Warning:</strong> You have reached your plan limit. Please delete a report or upgrade your plan to upload more.
            </p>
          )}
        </div>
      )}

      {/* Reports Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: '30%' }}>Report Name</th>
              <th style={{ width: '45%' }}>Report URL</th>
              <th style={{ width: '25%' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredReports.length === 0 ? (
              <tr>
                <td colSpan="3" style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                  No reports match your search.
                </td>
              </tr>
            ) : (
              filteredReports.map((report) => {
                const reportName = report.ReportName || report.reportName;
                const reportId = report.ReportID || report.reportID;
                const reportUrl = buildReportUrl(reportId);
                const isCopied = copiedReportId === reportId;

                return (
                  <tr key={reportId}>
                    <td style={{ fontWeight: '600' }}>{reportName}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="text"
                          value={reportUrl}
                          readOnly
                          style={{
                            flex: 1,
                            padding: '0.25rem 0.5rem',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            background: '#f8f9fa',
                            fontSize: '0.75rem',
                            color: '#495057',
                            fontFamily: 'monospace'
                          }}
                        />
                        <button
                          onClick={() => copyToClipboard(reportUrl, reportId)}
                          title="Copy URL to clipboard"
                          disabled={!workspaceId}
                          style={{
                            padding: '0.25rem 0.5rem',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            background: isCopied ? '#28a745' : 'white',
                            color: isCopied ? 'white' : '#495057',
                            cursor: workspaceId ? 'pointer' : 'not-allowed',
                            fontSize: '0.75rem',
                            transition: 'all 0.2s ease',
                            whiteSpace: 'nowrap',
                            opacity: workspaceId ? 1 : 0.5
                          }}
                        >
                          {isCopied ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button 
                          className="btn btn-primary"
                          onClick={() => window.open(reportUrl, '_blank')}
                          disabled={loading || !workspaceId || isSuspended}
                          style={{ 
                            fontSize: '0.8rem', 
                            padding: '0.25rem 0.5rem',
                            opacity: (workspaceId && !isSuspended) ? 1 : 0.5,
                            cursor: (workspaceId && !isSuspended) ? 'pointer' : 'not-allowed'
                          }}
                          title={isSuspended ? 'Disabled - Subscription Suspended' : 'Open report in new tab'}
                        >
                          &#x1F517; Open
                        </button>
                        <button 
                          className="btn btn-primary"
                          onClick={() => handleEmbeddingClick(report)}
                          disabled={loading || isSuspended}
                          style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                          title={isSuspended ? 'Disabled - Subscription Suspended' : 'View embedding information'}
                        >
                          Embed
                        </button>
                        <button 
                          className="btn btn-danger"
                          onClick={() => onDeleteReport(reportId)}
                          disabled={loading || isSuspended}
                          style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                          title={isSuspended ? 'Disabled - Subscription Suspended' : 'Delete Report'}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Upload Modal */}
      {showUploadModal && renderUploadModal()}

      {/* Embedding Info Modal */}
      {showEmbeddingModal && renderEmbeddingModal()}
    </div>
  );
};

export default ReportsList;
